import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { ARTICLE_TOPICS, Article, ArchitectureImage, ArticleTopic, Publications } from '../../data/profileData';
import { getFeaturedArticle } from '../../utils/contentStats';
import { getLargeImageUrl } from '../../utils/images';
import { Icon, Section } from '../ui';
import Lightbox, { LightboxImage } from '../Lightbox/Lightbox';
import ArticleItem from './ArticleItem';
import './Writing.css';

interface WritingProps {
  publications: Publications;
}

type TopicFilter = 'All' | ArticleTopic;

const ARTICLES_PER_PAGE = 6;

export const articleUrl = (id: string): string => `${window.location.origin}/writing/${id}`;

const byNewest = (a: Article, b: Article) => b.date.localeCompare(a.date);

const Writing: React.FC<WritingProps> = ({ publications }) => {
  const navigate = useNavigate();
  const slug = useMatch('/writing/:slug')?.params.slug ?? null;

  const articles = useMemo(() => [...publications.articles].sort(byNewest), [publications.articles]);
  const featured = useMemo(() => getFeaturedArticle(articles), [articles]);
  const openArticle = slug ? articles.find((article) => article.id === slug) ?? null : null;
  const notFound = slug !== null && openArticle === null;

  const [topic, setTopic] = useState<TopicFilter>('All');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [lightboxImage, setLightboxImage] = useState<LightboxImage | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const announcementTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => () => window.clearTimeout(announcementTimer.current), []);

  const isFiltering = topic !== 'All' || debouncedQuery !== '';
  // The featured card is shown on its own only when the full, unfiltered list is in view.
  const showFeatured = !isFiltering && featured !== undefined;

  const matchingArticles = useMemo(
    () =>
      articles.filter(
        (article) =>
          (topic === 'All' || article.tags.includes(topic)) &&
          (!debouncedQuery ||
            article.title.toLowerCase().includes(debouncedQuery) ||
            article.description.toLowerCase().includes(debouncedQuery))
      ),
    [articles, topic, debouncedQuery]
  );

  const listedArticles = showFeatured
    ? matchingArticles.filter((article) => article.id !== featured?.id)
    : matchingArticles;

  const totalPages = Math.max(1, Math.ceil(listedArticles.length / ARTICLES_PER_PAGE));
  const pageArticles = listedArticles.slice((page - 1) * ARTICLES_PER_PAGE, page * ARTICLES_PER_PAGE);

  const topicCounts = useMemo(() => {
    const counts: Record<TopicFilter, number> = { All: articles.length } as Record<TopicFilter, number>;
    for (const t of ARTICLE_TOPICS) counts[t] = articles.filter((article) => article.tags.includes(t)).length;
    return counts;
  }, [articles]);

  useEffect(() => setPage(1), [topic, debouncedQuery]);

  // An article opened by URL (direct link, Back/Forward) must be visible: if it is not on screen,
  // clear filters and jump to the page that holds it. Opening from the list never moves the view.
  const visibleIds = useRef<string[]>([]);
  visibleIds.current = [...(showFeatured && featured ? [featured.id] : []), ...pageArticles.map((a) => a.id)];
  const deepLinkedId = openArticle?.id;
  useEffect(() => {
    if (!deepLinkedId || visibleIds.current.includes(deepLinkedId)) return;
    setTopic('All');
    setQuery('');
    setDebouncedQuery('');
    const index = articles.filter((a) => a.id !== featured?.id).findIndex((a) => a.id === deepLinkedId);
    if (index >= 0) setPage(Math.floor(index / ARTICLES_PER_PAGE) + 1);
  }, [deepLinkedId, articles, featured]);

  // On first load of /writing/<slug>, bring the article (or the not-found notice) into view.
  const initialSlug = useRef(slug);
  useEffect(() => {
    if (!initialSlug.current) return;
    const targetId = notFound ? 'writing' : `article-${initialSlug.current}`;
    let cancelled = false;
    const scrollToTarget = () => {
      if (!cancelled) document.getElementById(targetId)?.scrollIntoView({ block: 'start', behavior: 'auto' });
    };
    const frame = window.requestAnimationFrame(scrollToTarget);
    // Web fonts and images above the article change its position as they arrive: scroll again once settled.
    document.fonts?.ready.then(scrollToTarget);
    if (document.readyState !== 'complete') window.addEventListener('load', scrollToTarget, { once: true });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('load', scrollToTarget);
    };
    // Only for the URL the page was loaded with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const announce = useCallback((message: string) => {
    setAnnouncement(message);
    window.clearTimeout(announcementTimer.current);
    announcementTimer.current = window.setTimeout(() => setAnnouncement(''), 3000);
  }, []);

  const toggleArticle = useCallback(
    (article: Article) => navigate(openArticle?.id === article.id ? '/' : `/writing/${article.id}`),
    [navigate, openArticle]
  );

  const copyLink = useCallback(
    async (article: Article) => {
      try {
        await navigator.clipboard.writeText(articleUrl(article.id));
        announce('Link copied');
      } catch {
        announce('Could not copy the link. Copy it from the address bar instead.');
      }
    },
    [announce]
  );

  const shareOnLinkedIn = useCallback((article: Article) => {
    const url = encodeURIComponent(articleUrl(article.id));
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,noreferrer');
  }, []);

  const openImage = useCallback((image: ArchitectureImage) => {
    setLightboxImage({ src: getLargeImageUrl(image.src), alt: image.alt, caption: image.caption });
  }, []);
  const closeImage = useCallback(() => setLightboxImage(null), []);

  const goToPage = (next: number) => {
    setPage(next);
    document.getElementById('writing-list')?.scrollIntoView({ block: 'start' });
  };

  const itemProps = { onToggle: toggleArticle, onCopyLink: copyLink, onShare: shareOnLinkedIn, onOpenImage: openImage };

  return (
    <Section
      id="writing"
      title="Writing"
      eyebrow="Ideas & articles"
      intro="Notes from the field on architecture, AI-assisted engineering, the Power Platform, and leading teams."
      className="writing"
    >
      {notFound && (
        <p className="writing__notice" role="status">
          Sorry, that article could not be found. It may have moved; browse the latest writing below.
        </p>
      )}

      {showFeatured && featured && (
        <ArticleItem article={featured} featured isOpen={openArticle?.id === featured.id} {...itemProps} />
      )}

      <div className="writing__controls">
        <div className="ui-filter" role="group" aria-label="Filter articles by topic">
          {(['All', ...ARTICLE_TOPICS] as TopicFilter[]).map((t) => (
            <button
              key={t}
              type="button"
              className="ui-filter__option"
              aria-pressed={topic === t}
              onClick={() => setTopic(t)}
            >
              {t}
              <span className="ui-filter__count">{topicCounts[t]}</span>
            </button>
          ))}
        </div>

        <form className="writing__search" role="search" onSubmit={(e) => e.preventDefault()}>
          <label htmlFor="article-search" className="visually-hidden">
            Search articles
          </label>
          <Icon icon={Search} size={18} className="writing__search-icon" />
          <input
            id="article-search"
            type="search"
            placeholder="Search articles"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="writing__search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <Icon icon={X} size={16} />
            </button>
          )}
        </form>
      </div>

      <p className="visually-hidden" aria-live="polite">
        {isFiltering ? `${matchingArticles.length} article${matchingArticles.length === 1 ? '' : 's'} shown.` : ''}
      </p>

      <div id="writing-list" className="writing__list">
        {pageArticles.map((article) => (
          <ArticleItem key={article.id} article={article} isOpen={openArticle?.id === article.id} {...itemProps} />
        ))}
        {matchingArticles.length === 0 && <p className="writing__empty">No articles match your search.</p>}
      </div>

      {totalPages > 1 && (
        <nav className="writing__pagination" aria-label="Article pages">
          <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1} aria-label="Previous page">
            <Icon icon={ChevronLeft} size={18} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => goToPage(n)}
              aria-label={`Page ${n}`}
              aria-current={page === n ? 'page' : undefined}
            >
              {n}
            </button>
          ))}
          <button type="button" onClick={() => goToPage(page + 1)} disabled={page === totalPages} aria-label="Next page">
            <Icon icon={ChevronRight} size={18} />
          </button>
        </nav>
      )}

      <div className="writing__toast" role="status" aria-live="polite">
        {announcement}
      </div>

      <Lightbox image={lightboxImage} onClose={closeImage} />
    </Section>
  );
};

export default Writing;
